import React from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Star } from "lucide-react";

/**
 * Admin Reviews tab — extracted from AdminApp.jsx Dashboard.
 * Approve / delete customer reviews. Pending reviews highlighted with yellow border.
 */
export const ReviewsTab = ({ reviews, loading, fetchReviews, setError, setSuccess }) => {
  return (
    <>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white">⭐ Gestione Recensioni</h2>
        <p className="text-gray-400">Approva, modifica o elimina le recensioni dei clienti</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Caricamento...</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Star className="w-24 h-24 mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-gray-200 mb-2">Nessuna recensione</h3>
          <p className="text-gray-400">Le recensioni dei clienti appariranno qui!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id} className={review.status === "pending" ? "border-yellow-300" : ""}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-5 h-5 ${
                              star <= review.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
                            }`}
                          />
                        ))}
                      </div>
                      <Badge
                        variant={
                          review.status === "approved"
                            ? "default"
                            : review.status === "pending"
                              ? "secondary"
                              : "destructive"
                        }
                      >
                        {review.status === "approved"
                          ? "✅ Approvata"
                          : review.status === "pending"
                            ? "⏳ In attesa"
                            : "❌ Rifiutata"}
                      </Badge>
                      {review.verified_purchase && (
                        <Badge className="bg-green-100 text-green-800">✓ Acquisto Verificato</Badge>
                      )}
                    </div>

                    <h4 className="font-semibold text-lg mb-2">{review.customer_name}</h4>
                    <p className="text-gray-700 mb-3">{review.comment}</p>

                    <div className="text-sm text-gray-500">
                      <span>Email: {review.customer_email}</span>
                      <span className="mx-2">•</span>
                      <span>{new Date(review.created_at).toLocaleDateString("it-IT")}</span>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2 ml-4">
                    {review.status === "pending" && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={async () => {
                          try {
                            const token = sessionStorage.getItem("access_token");
                            await axios.put(
                              `${process.env.REACT_APP_BACKEND_URL}/api/reviews/${review.id}`,
                              { status: "approved" },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            fetchReviews();
                            setSuccess("Recensione approvata!");
                            setTimeout(() => setSuccess(""), 3000);
                          } catch (err) {
                            setError("Errore nell'approvazione");
                            setTimeout(() => setError(""), 3000);
                          }
                        }}
                      >
                        ✅ Approva
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        if (window.confirm("Sei sicuro di voler eliminare questa recensione?")) {
                          try {
                            const token = sessionStorage.getItem("access_token");
                            await axios.delete(
                              `${process.env.REACT_APP_BACKEND_URL}/api/reviews/${review.id}`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            fetchReviews();
                            setSuccess("Recensione eliminata!");
                            setTimeout(() => setSuccess(""), 3000);
                          } catch (err) {
                            setError("Errore nell'eliminazione");
                            setTimeout(() => setError(""), 3000);
                          }
                        }
                      }}
                    >
                      🗑️ Elimina
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};
